import {gql} from '@apollo/client';
import React from 'react';

import {assert} from 'Asserts';
import {Collaborator} from 'auth/PermissionConstants';
import SchemaCollaboratorsSection from 'collaborator-components/SchemaCollaboratorsSection';
import {useSchemaCollaboratorsSection} from 'collaborator-components/useSchemaCollaboratorsSection';
import {TagSchemaAuthParentOption} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  TagSchemaPermissionSettingsQuery,
  TagSchemaPermissionSettingsQueryVariables,
} from 'settings/registry/__generated/TagSchemaPermissionSettingsQuery';
import * as TagSchemaActions from 'tag-schema/TagSchemaActions';
import getTestProps from 'test/lib/cypress/getTestProps';
import {Header2} from 'ui-library/Headers';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

type TagSchema = ExtractByTypename<TagSchemaPermissionSettingsQuery['tagSchema'], 'TagSchemaNode'>;

export const TAG_SCHEMA_PERMISSION_SETTINGS_QUERY = gql`
  query TagSchemaPermissionSettingsQuery($tagSchemaId: StrictString!)
  @skipAudit
  @engComponent(name: REGISTRY) {
    tagSchema(id: $tagSchemaId) {
      ... on TagSchemaNode {
        id
        authParentOption
        permissions {
          ...SchemaPermissionsGraphQL
        }
        ...CollaboratorInfoGraphQL
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('CollaboratorInfo').fragment}
  ${getFragmentWrapper('SchemaPermissions').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface TagSchemaPermissionSettingsProps {
  id?: string;
  collaborators: Array<Collaborator>;
  isReadOnly: boolean;
  authParentOption?: TagSchemaAuthParentOption;
  onUpdate: (collaborators: Array<Collaborator>) => void;
  onSubmit?: () => Promise<void>;
}
export function TagSchemaPermissionSettings({
  id,
  collaborators,
  isReadOnly,
  authParentOption,
  onUpdate,
  onSubmit,
}: TagSchemaPermissionSettingsProps): JSX.Element {
  return (
    <div
      className="settingsPage-contentWrapper"
      {...getTestProps({component: 'TagSchemaPermissionSettings', element: 'root'})}
    >
      <SchemaCollaboratorsSection
        policeableId={id}
        collaborators={collaborators}
        isReadOnly={isReadOnly}
        onUpdate={onUpdate}
        onSubmit={onSubmit}
      />
      {authParentOption && (
        <div className="u-large-spacing-top">
          <Header2>Registering Entities</Header2>
          <p>
            Users with the Create or Admin Access Policy can register objects of this schema. The registered
            objects of this schema will have permissions that are{' '}
            <strong>{authParentOption === TagSchemaAuthParentOption.FOLDER ? 'Registry' : 'Project'}</strong>
            -based.
          </p>
        </div>
      )}
    </div>
  );
}

export interface QueryingProps {
  tagSchemaId: string;
}
function QueryingTagSchemaPermissionSettings({tagSchemaId}: QueryingProps): JSX.Element | null {
  const result = useBenchlingQuery<
    TagSchemaPermissionSettingsQuery,
    TagSchemaPermissionSettingsQueryVariables
  >(TAG_SCHEMA_PERMISSION_SETTINGS_QUERY, {
    variables: {tagSchemaId},
  });
  if (result.type === QueryResultType.ERROR) {
    return null;
  } else if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else {
    assert(!isInaccessible(result.data.tagSchema), 'Expected tag schema to be accessible.');
    return (
      <ExistingTagSchemaPermissionSettings
        tagSchema={result.data.tagSchema}
        refetch={async () => {
          await result.refetch();
        }}
      />
    );
  }
}

export function ExistingTagSchemaPermissionSettings({
  tagSchema,
  refetch,
}: {
  tagSchema: TagSchema;
  refetch: () => Promise<void>;
}): JSX.Element {
  const [{collaborators, minimalCollaborators}, handleUpdate] = useSchemaCollaboratorsSection(
    tagSchema.collaboratorsJson
  );
  return (
    <TagSchemaPermissionSettings
      id={tagSchema.id}
      isReadOnly={!tagSchema.permissions.definitionEditable}
      authParentOption={tagSchema.authParentOption}
      collaborators={collaborators}
      onUpdate={handleUpdate}
      onSubmit={async () => {
        await TagSchemaActions.updateTagSchemaCollaborators(tagSchema.id, {
          collaborators: minimalCollaborators,
        });
        await refetch();
      }}
    />
  );
}

export default QueryingTagSchemaPermissionSettings;
